import React, { useEffect, useState } from 'react';
import CustomZoomOriginPicker from '../games/custom/CustomZoomOriginPicker';
import {
  DAILY_INTERPOLATION_INITIAL_ZOOM,
  DAILY_ZOOM_BOUNDS,
} from '../../constants/games/dailyGame';
import './DailyZoomOriginEditorModal.css';

const clamp01 = (value) => Math.min(1, Math.max(0, value));
const toCoordinateOr = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? clamp01(parsed) : fallback;
};

const toSafeZoomOrigin = (row) => {
  const x = Number(row?.zoom_origin_x);
  const y = Number(row?.zoom_origin_y);
  return {
    x: Number.isFinite(x) ? clamp01(x) : 0.5,
    y: Number.isFinite(y) ? clamp01(y) : 0.5,
  };
};

const DailyZoomOriginEditorModal = ({ row, busy = false, onClose, onSave }) => {
  const [draftOrigin, setDraftOrigin] = useState(() => toSafeZoomOrigin(row));

  useEffect(() => {
    if (!row) return;
    setDraftOrigin(toSafeZoomOrigin(row));
  }, [row?.id, row?.puzzle_date, row?.zoom_origin_x, row?.zoom_origin_y]);

  useEffect(() => {
    if (!row) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !busy) {
        onClose?.();
      }
    };
    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousBodyOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [row, busy, onClose]);

  const puzzleDate = typeof row?.puzzle_date === 'string' ? row.puzzle_date : '';
  const imageUrl = typeof row?.image_url === 'string' ? row.image_url.trim() : '';

  if (!row || !puzzleDate || !imageUrl) {
    return null;
  }

  const onManualCoordinateChange = (axis, rawValue) => {
    const parsed = Number(rawValue);
    if (!Number.isFinite(parsed)) return;
    setDraftOrigin((previous) => ({
      ...previous,
      [axis]: clamp01(parsed),
    }));
  };

  const onSubmit = () => {
    onSave?.({
      puzzleDate,
      zoomOriginX: draftOrigin.x,
      zoomOriginY: draftOrigin.y,
    });
  };

  return (
    <div
      className="daily-zoom-origin-modal__overlay"
      role="dialog"
      aria-modal="true"
      aria-label={`Adjust daily start zoom for ${puzzleDate}`}
      onClick={busy ? undefined : onClose}
    >
      <div className="daily-zoom-origin-modal__content" onClick={(event) => event.stopPropagation()}>
        <header className="daily-zoom-origin-modal__header">
          <h4>Adjust Start Zoom for {puzzleDate}</h4>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="daily-zoom-origin-modal__close"
            aria-label="Close zoom origin editor"
          >
            x
          </button>
        </header>

        <p className="daily-zoom-origin-modal__hint">
          Click or drag on the image to set where players start zoomed in.
        </p>

        <CustomZoomOriginPicker
          imageUrl={imageUrl}
          value={draftOrigin}
          zoomLevel={DAILY_INTERPOLATION_INITIAL_ZOOM}
          zoomBounds={DAILY_ZOOM_BOUNDS}
          disabled={busy}
          onChange={(next) =>
            setDraftOrigin((previous) => ({
              x: toCoordinateOr(next?.x, previous.x),
              y: toCoordinateOr(next?.y, previous.y),
            }))
          }
        />

        <div className="daily-zoom-origin-modal__coords">
          <label>
            X (0..1)
            <input
              type="number"
              min="0"
              max="1"
              step="0.01"
              value={draftOrigin.x}
              onChange={(event) => onManualCoordinateChange('x', event.target.value)}
              disabled={busy}
            />
          </label>
          <label>
            Y (0..1)
            <input
              type="number"
              min="0"
              max="1"
              step="0.01"
              value={draftOrigin.y}
              onChange={(event) => onManualCoordinateChange('y', event.target.value)}
              disabled={busy}
            />
          </label>
        </div>

        <div className="daily-zoom-origin-modal__actions">
          <button type="button" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" onClick={onSubmit} disabled={busy}>
            {busy ? 'Saving...' : 'Save Start Zoom'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DailyZoomOriginEditorModal;
